/**
 * The only place the console knows how it authenticates.
 *
 * Every request carries the operator key, and any 401 — from any endpoint, at any time —
 * logs out. That second half matters: the password can be changed under a running
 * session, and the honest response to "this key no longer works" is to stop pretending
 * it does rather than to fail one screen at a time.
 */

import { fetchBaseQuery, type BaseQueryFn } from '@reduxjs/toolkit/query'
import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { loggedOut, persistKey } from '~/app/authSlice'
import type { AuthState } from '~/app/authSlice'

export const OPERATOR_KEY_HEADER = 'X-Voe-Operator-Key'

/**
 * Same origin in production (FastAPI serves this build), proxied by Vite in development —
 * so this is the whole of the environment difference, and there is no CORS in either.
 *
 * Resolved against the current origin rather than left as the bare path `/api`, because a
 * relative URL is something only a browser knows how to complete: `fetch` outside one
 * refuses it outright, which would make every request untestable.
 */
export const API_BASE = new URL('/api', globalThis.location?.origin ?? 'http://localhost').href

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE,
  prepareHeaders: (headers, { getState }) => {
    const { key } = (getState() as { auth: AuthState }).auth
    if (key) headers.set(OPERATOR_KEY_HEADER, key)
    return headers
  },
})

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions)
  if (result.error?.status === 401) {
    persistKey(null)
    api.dispatch(loggedOut())
  }
  return result
}

/**
 * The HTTP status a rejected request came back with, where there was one.
 *
 * For the refusals that are not failures. A 409 saying "that run is already in flight" is
 * the engine working, and drawing it in fault colours beside a button tells the operator
 * they broke something when what they need is the run that is already going.
 */
export function errorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined
  const fetchError = error as FetchBaseQueryError
  return 'status' in fetchError && typeof fetchError.status === 'number'
    ? fetchError.status
    : undefined
}

/** Reads the message the backend actually sent, so errors say what went wrong. */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (!error || typeof error !== 'object') return fallback
  const fetchError = error as FetchBaseQueryError
  if ('status' in fetchError) {
    const data = fetchError.data as { detail?: string } | undefined
    if (data?.detail) return data.detail
    if (fetchError.status === 'FETCH_ERROR') return 'Cannot reach the engine'
    if (typeof fetchError.status === 'number') return `Request failed (${fetchError.status})`
  }
  return fallback
}
